import { useState, useEffect, useCallback } from 'react';
import { BASE, authFetch } from '../constants';
import { useToast } from '../contexts/ToastContext';

const API = `${BASE}/api/v1/auto-approve`;

interface ProvisionalJE {
  je_id: number;
  je_number: string;
  exception_ref: string;
  entity_code: string;
  amount: number;
  currency: string;
  je_status: string;
  posting_date: string;
  confirmation_deadline: string;
  hours_remaining: number | null;
  created_at: string;
  line_count: number;
}

interface AuditCheck { name: string; passed: boolean; detail: string; }
interface LayerResult { passed?: boolean; checks?: AuditCheck[]; reason?: string; [key: string]: any; }
interface AuditRecord {
  id: number;
  exception_id: number;
  decision: string;
  blocked_at_layer: number | null;
  blocked_reason: string | null;
  layer_1_eligibility: LayerResult;
  layer_2_ai_resolution: LayerResult;
  layer_3_validation: LayerResult;
  layer_4_sil_gate: LayerResult;
  layer_5_risk_gate: LayerResult;
  layer_6_accounting: LayerResult;
  layer_7_provisional: LayerResult | null;
  processed_at: string;
}

const LAYER_NAMES: Record<string, string> = {
  layer_1_eligibility: 'L1: Eligibility',
  layer_2_ai_resolution: 'L2: AI Resolution',
  layer_3_validation: 'L3: Validation',
  layer_4_sil_gate: 'L4: SIL Gate',
  layer_5_risk_gate: 'L5: Risk Gate',
  layer_6_accounting: 'L6: Accounting',
  layer_7_provisional: 'L7: Provisional Post',
};

export default function ProvisionalReview() {
  const { toast } = useToast();
  const [jes, setJes] = useState<ProvisionalJE[]>([]);
  const [selected, setSelected] = useState<ProvisionalJE | null>(null);
  const [audit, setAudit] = useState<AuditRecord | null>(null);
  const [expandedLayer, setExpandedLayer] = useState<string | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadJes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/provisional-review`);
      setJes(await res.json());
    } catch (e) { console.error(e); toast({ message: 'Failed to load provisional JEs.', type: 'error' }); }
    setLoading(false);
  }, []);

  useEffect(() => { loadJes(); }, [loadJes]);

  const loadAudit = async (excRef: string) => {
    try {
      const excId = parseInt(excRef);
      if (isNaN(excId)) return;
      const res = await authFetch(`${API}/audit/${excId}`);
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) setAudit(data[0]);
    } catch (e) { console.error(e); toast({ message: 'Failed to load audit trail.', type: 'error' }); }
  };

  const handleSelect = (je: ProvisionalJE) => {
    setSelected(je);
    setAudit(null);
    setExpandedLayer(null);
    loadAudit(je.exception_ref);
  };

  const handleConfirm = async (jeId: number) => {
    const res = await authFetch(`${API}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ je_ids: [jeId], confirmed_by: 'reviewer' }),
    });
    if (res.ok) {
      setSelected(null);
      loadJes();
    }
  };

  const handleReverse = async () => {
    if (!selected || !reverseReason.trim()) return;
    const res = await authFetch(`${API}/reverse`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ je_id: selected.je_id, reversed_by: 'reviewer', reason: reverseReason }),
    });
    if (res.ok) {
      setShowReverseModal(false);
      setReverseReason('');
      setSelected(null);
      loadJes();
    }
  };

  const deadlineColor = (hrs: number | null) => {
    if (hrs === null) return 'var(--text-dim)';
    if (hrs > 24) return 'var(--positive)';
    if (hrs > 0) return 'var(--warning)';
    return '#ef4444';
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1440, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Provisional Review</h1>
      <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 24 }}>
        Auto-approved JEs with 48-hour confirmation window. Confirm to finalize or reverse to reopen.
      </p>

      {jes.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--bg-border)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dim)', marginBottom: 12 }}>No provisional JEs awaiting review</div>
          <div style={{ fontSize: 12, color: 'var(--text-dimmer)', lineHeight: 1.8, maxWidth: 520, margin: '0 auto' }}>
            Provisional JEs appear here when the Auto-Approval Engine processes exceptions that pass all 7 veto layers
            (eligibility, AI resolution, validation, SIL gate, risk gate, accounting rules, and provisional posting).
            <br /><br />
            Each provisional JE has a <strong style={{ color: 'var(--text-dim)' }}>48-hour confirmation window</strong> during which a human reviewer
            must either <strong style={{ color: 'var(--positive)' }}>Confirm</strong> (finalize) or <strong style={{ color: '#ef4444' }}>Reverse</strong> (reopen for manual review).
            If no action is taken within 48 hours, drift detection flags the JE for review.
            <br /><br />
            Exceptions that fail any veto layer are routed directly to <strong style={{ color: 'var(--text-dim)' }}>My Inbox</strong> for human review.
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 16 }}>
          {/* Left: JE list */}
          <div>
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--bg-border)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.03)' }}>
                    <th style={th}>Exception</th>
                    <th style={th}>Amount</th>
                    <th style={th}>Entity</th>
                    <th style={th}>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {jes.map(je => (
                    <tr
                      key={je.je_id}
                      onClick={() => handleSelect(je)}
                      style={{
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(0,0,0,0.04)',
                        background: selected?.je_id === je.je_id ? 'rgba(111,66,193,0.08)' : undefined,
                      }}
                    >
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>#{je.exception_ref}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>{je.je_number}</div>
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                        {je.currency} {je.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td style={td}>{je.entity_code}</td>
                      <td style={{ ...td, color: deadlineColor(je.hours_remaining) }}>
                        {je.hours_remaining !== null
                          ? je.hours_remaining > 0 ? `${je.hours_remaining.toFixed(0)}h` : 'Overdue'
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: Detail + audit trail */}
          {selected && (
            <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--bg-border)', padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Exception #{selected.exception_ref}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{selected.je_number}</div>
                </div>
                <span style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                  background: 'rgba(251,191,36,0.12)', color: 'var(--warning)',
                }}>
                  PROVISIONAL
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16, fontSize: 12 }}>
                <div><span style={{ color: 'var(--text-dim)' }}>Amount:</span> <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{selected.currency} {selected.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                <div><span style={{ color: 'var(--text-dim)' }}>Entity:</span> <span style={{ color: 'var(--text-primary)' }}>{selected.entity_code}</span></div>
                <div><span style={{ color: 'var(--text-dim)' }}>Lines:</span> <span style={{ color: 'var(--text-primary)' }}>{selected.line_count}</span></div>
                <div>
                  <span style={{ color: 'var(--text-dim)' }}>Deadline:</span>{' '}
                  <span style={{ color: deadlineColor(selected.hours_remaining), fontWeight: 600 }}>
                    {selected.hours_remaining !== null
                      ? selected.hours_remaining > 0 ? `${selected.hours_remaining.toFixed(1)}h remaining` : 'OVERDUE'
                      : '-'}
                  </span>
                </div>
              </div>

              {/* 7-Layer Audit Trail */}
              {audit && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 8, textTransform: 'uppercase' }}>7-Layer Audit Trail</div>
                  {Object.entries(LAYER_NAMES).map(([key, label]) => {
                    const layer = (audit as any)[key] as LayerResult | null;
                    if (!layer || Object.keys(layer).length === 0) return (
                      <div key={key} style={{ padding: '8px 10px', marginBottom: 2, background: 'rgba(0,0,0,0.02)', borderRadius: 4, fontSize: 11, color: 'var(--text-dim)' }}>
                        {label} — not reached
                      </div>
                    );
                    const passed = layer.passed !== false;
                    const isExpanded = expandedLayer === key;
                    return (
                      <div key={key} style={{ marginBottom: 2 }}>
                        <div
                          onClick={() => setExpandedLayer(isExpanded ? null : key)}
                          style={{
                            padding: '8px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11,
                            background: passed ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)',
                            border: `1px solid ${passed ? '#f0fdf4' : '#fef2f2'}`,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          }}
                        >
                          <span style={{ color: passed ? 'var(--positive)' : '#fca5a5', fontWeight: 600 }}>
                            {passed ? '[PASS]' : '[FAIL]'} {label}
                          </span>
                          <span style={{ color: 'var(--text-dim)', fontSize: 10 }}>{isExpanded ? 'v' : '>'}</span>
                        </div>
                        {isExpanded && (
                          <div style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.02)', borderBottomLeftRadius: 4, borderBottomRightRadius: 4, fontSize: 10 }}>
                            {layer.checks?.map((c: AuditCheck, i: number) => (
                              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 3 }}>
                                <span style={{ color: c.passed ? 'var(--positive)' : '#ef4444', fontWeight: 700 }}>{c.passed ? 'OK' : 'X'}</span>
                                <span style={{ color: 'var(--navy-800)', minWidth: 110 }}>{c.name}</span>
                                <span style={{ color: 'var(--text-dim)' }}>{c.detail}</span>
                              </div>
                            ))}
                            {layer.reason && !layer.checks && (
                              <div style={{ color: '#fca5a5' }}>{layer.reason}</div>
                            )}
                            {!layer.checks && !layer.reason && (
                              <div style={{ color: 'var(--text-dim)' }}>
                                {JSON.stringify(layer, null, 2).substring(0, 300)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => handleConfirm(selected.je_id)}
                  style={{ flex: 1, padding: '10px 0', background: '#f0fdf4', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: 'var(--positive)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  Confirm
                </button>
                <button
                  onClick={() => setShowReverseModal(true)}
                  style={{ flex: 1, padding: '10px 0', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#ef4444', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  Reverse
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reverse modal */}
      {showReverseModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, padding: 24, width: 400, border: '1px solid var(--bg-border)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>Reverse JE #{selected?.je_number}</h3>
            <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>
              This will create a reversal JE and reopen the exception for human review.
            </p>
            <textarea
              value={reverseReason}
              onChange={e => setReverseReason(e.target.value)}
              placeholder="Reason for reversal (required)"
              style={{ width: '100%', minHeight: 80, background: 'var(--bg-surface)', border: '1px solid var(--bg-border)', borderRadius: 6, padding: 10, color: 'var(--text-primary)', fontSize: 12, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button
                onClick={() => { setShowReverseModal(false); setReverseReason(''); }}
                style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--bg-border)', borderRadius: 6, color: 'var(--text-dim)', cursor: 'pointer', fontSize: 12 }}
              >
                Cancel
              </button>
              <button
                onClick={handleReverse}
                disabled={!reverseReason.trim()}
                style={{ flex: 1, padding: 10, background: reverseReason.trim() ? '#fef2f2' : 'rgba(0,0,0,0.03)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: reverseReason.trim() ? '#ef4444' : 'var(--text-dim)', cursor: reverseReason.trim() ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 700 }}
              >
                Confirm Reversal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 12, color: 'var(--text-secondary)' };
